/**
 * Translate page — AI-powered translation of Excel selection.
 *
 * Reads selected cells, sends them to the custom function backend
 * for translation, and writes the result to adjacent cells.
 */

import React, { useState } from 'react';
import {
  Button,
  Text,
  Badge,
  Dropdown,
  Option,
  Textarea,
} from '@fluentui/react-components';
import {
  DocumentSearchRegular,
  TranslateRegular,
  ArrowResetRegular,
} from '@fluentui/react-icons';
import { executeCustomFunction } from '../services/api.client';
import { readSelectionInfo, writeValuesAdjacent, type SelectionInfo } from '../office';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';

const LANGUAGES = [
  { code: 'EN', label: 'Anglais' },
  { code: 'FR', label: 'Francais' },
  { code: 'ES', label: 'Espagnol' },
  { code: 'DE', label: 'Allemand' },
  { code: 'IT', label: 'Italien' },
  { code: 'PT', label: 'Portugais' },
  { code: 'NL', label: 'Neerlandais' },
  { code: 'ZH', label: 'Chinois' },
  { code: 'JA', label: 'Japonais' },
  { code: 'AR', label: 'Arabe' },
] as const;

export const TranslatePage: React.FC = () => {
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [targetLang, setTargetLang] = useState('EN');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<string[]>();
  const [error, setError] = useState<string>();
  const [written, setWritten] = useState(false);

  const resetAll = () => {
    setSelectionInfo(undefined);
    setResults(undefined);
    setError(undefined);
    setWritten(false);
  };

  const handleReadSelection = async () => {
    setError(undefined);
    setResults(undefined);
    setWritten(false);
    setLoading(true);

    try {
      const info = await readSelectionInfo();
      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule selectionnee.');
        setSelectionInfo(undefined);
        return;
      }
      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async () => {
    if (!selectionInfo) return;
    const startedAt = Date.now();
    setError(undefined);
    setResults(undefined);
    setWritten(false);
    setLoading(true);

    const settings = getSettings();

    try {
      // Flatten selection to an array of texts (first column or all cells)
      const texts: string[] = selectionInfo.values
        .map((row) => {
          const cell = (row as unknown[])[0];
          return cell == null ? '' : String(cell);
        })
        .filter((t) => t.trim() !== '');

      if (texts.length === 0) {
        setError('La selection ne contient que des cellules vides.');
        setLoading(false);
        return;
      }

      // Translate each text (sequential to avoid rate limits)
      const translated: string[] = [];
      for (const text of texts) {
        const result = await executeCustomFunction({
          functionName: 'AI_TRANSLATE',
          args: [text, 'auto', targetLang],
        });
        translated.push(result.value);
      }

      setResults(translated);
      addHistoryEntry(
        'custom_function',
        settings.provider,
        settings.model,
        `Traduction → ${targetLang} (${texts.length} cellules)`,
        true,
        undefined,
        {
          module: 'translate',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      addHistoryEntry(
        'custom_function',
        settings.provider,
        settings.model,
        `Traduction → ${targetLang}`,
        false,
        (err as Error).message,
        {
          module: 'translate',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWriteResults = async () => {
    if (!results) return;
    try {
      const values2D = results.map((r) => [r]);
      await writeValuesAdjacent(values2D);
      setWritten(true);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div>
      <div className="card">
        <Text className="section-title">Traduire la selection</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          Selectionnez des cellules contenant du texte. L&apos;IA traduira chaque cellule
          dans la langue cible et proposera d&apos;ecrire le resultat dans la colonne adjacente.
        </Text>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
          <div>
            <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 4 }}>Langue cible</Text>
            <Dropdown
              value={LANGUAGES.find((l) => l.code === targetLang)?.label ?? targetLang}
              onOptionSelect={(_e, data) => { if (data.optionValue) setTargetLang(data.optionValue); }}
              size="small"
              style={{ minWidth: 140 }}
            >
              {LANGUAGES.map((lang) => (
                <Option key={lang.code} value={lang.code}>{lang.label}</Option>
              ))}
            </Dropdown>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            size="small"
            appearance="primary"
            icon={<DocumentSearchRegular />}
            onClick={handleReadSelection}
            disabled={loading}
          >
            Lire la selection
          </Button>
          {selectionInfo && !loading && (
            <Button
              size="small"
              appearance="primary"
              icon={<TranslateRegular />}
              onClick={handleTranslate}
            >
              Traduire
            </Button>
          )}
          {(selectionInfo || results) && (
            <Button size="small" appearance="subtle" icon={<ArrowResetRegular />} onClick={resetAll}>
              Reinitialiser
            </Button>
          )}
        </div>

        {selectionInfo && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge appearance="outline" size="small" color="informative">
              {selectionInfo.address}
            </Badge>
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              {selectionInfo.rowCount} cellule{selectionInfo.rowCount > 1 ? 's' : ''}
            </Text>
          </div>
        )}
      </div>

      {loading && <LoadingSpinner label="Traduction en cours..." />}
      {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}

      {results && (
        <div className="card">
          <Text className="section-title">Resultats ({results.length})</Text>
          <Textarea
            value={results.join('\n')}
            readOnly
            resize="vertical"
            style={{ width: '100%', minHeight: 120, fontFamily: 'inherit', marginBottom: 8 }}
          />
          <Button
            size="small"
            appearance="primary"
            onClick={handleWriteResults}
            disabled={written}
          >
            {written ? 'Ecrit dans la colonne adjacente' : 'Ecrire dans la colonne adjacente'}
          </Button>
          {written && (
            <Badge appearance="tint" color="success" size="small" style={{ marginLeft: 8 }}>OK</Badge>
          )}
        </div>
      )}
    </div>
  );
};
